import { useEffect, useState } from "react";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  TrendingUp,
  Award,
  Building,
  Camera,
  Play,
  X,
  Eye,
} from "lucide-react";
import { type Project, type ProjectMetric } from "../../services/api";

interface ProgramDetailPageProps {
  program: Project;
  onBack: () => void;
}

export default function ProgramDetailPage({
  program,
  onBack,
}: ProgramDetailPageProps) {
  const [selectedBanner, setSelectedBanner] = useState(0);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const formatCurrency = (val: string | number) => {
    const num = Number(val);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const parseMetrics = (
    metricsRaw: string | ProjectMetric[] | null | undefined
  ): ProjectMetric[] => {
    if (!metricsRaw) return [];
    if (typeof metricsRaw === "string") {
      try {
        const parsed = JSON.parse(metricsRaw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(metricsRaw) ? metricsRaw : [];
  };

  const isVideoUrl = (url: string) => {
    if (!url) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
  };

  const getBeforeUrls = (): string[] => {
    if (!program.before_media) return [];
    return Array.isArray(program.before_media)
      ? program.before_media.filter(Boolean)
      : typeof program.before_media === "string" && program.before_media.trim()
        ? [program.before_media]
        : [];
  };

  const getAfterUrls = (): string[] => {
    if (!program.after_media) return [];
    return Array.isArray(program.after_media)
      ? program.after_media.filter(Boolean)
      : typeof program.after_media === "string" && program.after_media.trim()
        ? [program.after_media]
        : [];
  };

  const getBannerUrls = (): string[] => {
    if (!program.banner_image) return [];
    return Array.isArray(program.banner_image)
      ? program.banner_image.filter(Boolean)
      : typeof program.banner_image === "string" && program.banner_image.trim()
        ? [program.banner_image]
        : [];
  };
  const mobilised = Number(program.jbf_funds_mobilised) || 0;
  const required = Number(program.funds_required) || 0;
  const progressPct =
    required > 0 ? Math.min(100, Math.round((mobilised / required) * 100)) : 0;

  return (
    <div className="min-h-screen bg-[#FFF9F0] pb-20">
      {/* Sticky Header with Back Button */}
      <div className="sticky top-[5rem] z-50 border-b border-zinc-100 bg-white/90 backdrop-blur-md lg:top-[8.5rem]">
        <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="group flex items-center gap-2 text-zinc-600 transition-colors hover:text-[#7A1E1E]"
            >
              <ArrowLeft className="h-5 w-5 text-[#7A1E1E] transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-bold sm:text-base">
                Back to Programs & Initiatives
              </span>
            </button>
            <div>
              <span className="rounded-full border border-[#7A1E1E]/20 bg-[#7A1E1E]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#7A1E1E]">
                {program.status || "In Progress"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-8">
        <article className="overflow-hidden rounded-3xl border border-zinc-200/50 bg-white shadow-xl">
          {/* Banner Gallery */}
          <div className="overflow-hidden bg-zinc-950">
            {getBannerUrls().length > 0 ? (
              <>
                <div className="relative flex aspect-[21/8] items-center justify-center bg-zinc-950">
                  {isVideoUrl(getBannerUrls()[selectedBanner]) ? (
                    <video
                      src={getBannerUrls()[selectedBanner]}
                      className="h-full w-full object-contain"
                      controls
                      autoPlay
                      muted
                    />
                  ) : (
                    <img
                      src={getBannerUrls()[selectedBanner]}
                      alt={program.title}
                      className="h-full w-full object-cover opacity-90"
                    />
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/90 via-black/30 to-transparent p-6 sm:p-10">
                    <div className="max-w-2xl text-white">
                      <span className="text-xs font-bold uppercase tracking-widest text-amber-400">
                        Ongoing Initiative
                      </span>
                      <h1 className="mt-1 text-2xl font-extrabold leading-tight sm:text-4xl">
                        {program.title}
                      </h1>
                    </div>
                    <div className="flex items-center gap-1.5 rounded-lg bg-black/50 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Camera className="h-4 w-4" />
                      <span>
                        {selectedBanner + 1} / {getBannerUrls().length}
                      </span>
                    </div>
                  </div>
                </div>
                {getBannerUrls().length > 1 && (
                  <div className="scrollbar-hide flex gap-3 overflow-x-auto border-t border-white/10 bg-white p-4 backdrop-blur-md">
                    {getBannerUrls().map((image, index) => {
                      const isVideo = isVideoUrl(image);
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedBanner(index)}
                          className={`relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all ${selectedBanner === index
                            ? "border-[#D4A017]"
                            : "border-zinc-700 hover:border-zinc-500"
                            }`}
                        >
                          {isVideo ? (
                            <div className="relative h-full w-full bg-black">
                              <video
                                src={image}
                                className="h-full w-full object-cover opacity-50"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="h-6 w-6 fill-white/20 text-white" />
                              </div>
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={`View ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="relative flex aspect-[21/8] w-full flex-col items-center justify-center bg-gradient-to-br from-[#7A1E1E] to-zinc-900 p-6 text-white/40">
                <Building className="mb-2 h-16 w-16 text-white/20" />
                <h1 className="text-center text-2xl font-extrabold leading-tight text-white sm:text-4xl">
                  {program.title}
                </h1>
              </div>
            )}
          </div>

          {/* Content columns */}
          <div className="space-y-8 p-6 sm:p-12">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              {/* Left Main Content */}
              <div className="space-y-8 lg:col-span-8">
                <div>
                  <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-400">
                    Short Description
                  </h2>
                  <p className="whitespace-pre-wrap text-lg font-medium leading-relaxed text-zinc-700">
                    {program.short_description}
                  </p>
                </div>

                {program.long_description && (
                  <div className="border-t pt-8">
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-widest text-zinc-400">
                      Detailed Overview
                    </h2>
                    <div
                      className="prose prose-zinc max-w-none text-base leading-relaxed text-zinc-600"
                      dangerouslySetInnerHTML={{
                        __html: program.long_description,
                      }}
                    />
                  </div>
                )}

                {program.outcomes && (
                  <div className="rounded-2xl border border-t border-zinc-100 bg-zinc-50/50 p-6 pt-8">
                    <h2 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-500">
                      <Award className="h-4 w-4 text-[#7A1E1E]" /> Expected
                      Outcomes & Impact
                    </h2>
                    <p className="whitespace-pre-wrap text-base leading-relaxed text-zinc-700">
                      {program.outcomes}
                    </p>
                  </div>
                )}
              </div>

              {/* Right Sidebar Info */}
              <div className="space-y-8 border-l border-zinc-100 pl-0 lg:col-span-4 lg:pl-8">
                {/* Funding progress */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                    Financial Summary
                  </h3>
                  <div className="space-y-4 rounded-2xl border border-amber-100 bg-[#FFF9F0] p-5">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-semibold text-zinc-600">
                        JBF Mobilised
                      </span>
                      <span className="text-lg font-extrabold text-[#7A1E1E]">
                        {progressPct}%
                      </span>
                    </div>
                    {/* Progress slider */}
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-zinc-200">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#7A1E1E] to-[#B8900F]"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <div className="flex flex-col gap-1 border-t border-amber-200/40 pt-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-zinc-500">
                          Funds Mobilised:
                        </span>
                        <span className="font-bold text-emerald-800">
                          {formatCurrency(program.jbf_funds_mobilised)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-zinc-500">
                          Target Required:
                        </span>
                        <span className="font-bold text-zinc-900">
                          {formatCurrency(program.funds_required)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location & Map links */}
                <div className="space-y-4 border-t border-zinc-100 pt-8">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                    Location Info
                  </h3>
                  <div className="flex gap-2.5 text-zinc-600">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#7A1E1E]" />
                    <div className="flex flex-col text-sm leading-relaxed">
                      <span className="font-semibold text-zinc-800">
                        {program.address_line_1}
                      </span>
                      {program.address_line_2 && (
                        <span>{program.address_line_2}</span>
                      )}
                      <span>
                        {program.city_name || program.city},{" "}
                        {program.district_name
                          ? `${program.district_name}, `
                          : ""}
                        {program.state_name || program.state} -{" "}
                        {program.pincode}
                      </span>
                      <span>{program.country}</span>

                      {program.map_link && (
                        <a
                          href={program.map_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 self-start rounded-md bg-[#7A1E1E]/5 px-2.5 py-1 text-xs font-bold text-[#7A1E1E] hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Open Google
                          Maps
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contacts */}
                <div className="space-y-4 border-t border-zinc-100 pt-8">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                    Contact details
                  </h3>
                  <div className="space-y-3 text-sm text-zinc-600">
                    <div className="flex items-center gap-2.5">
                      <Phone className="h-4 w-4 shrink-0 text-[#7A1E1E]" />
                      <span>{program.mobile_no}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Mail className="h-4 w-4 shrink-0 text-[#7A1E1E]" />
                      <span className="truncate">{program.email}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact Metrics table */}
            {parseMetrics(program.project_metrics).length > 0 && (
              <div className="border-t border-zinc-100 pt-8">
                <h3 className="mb-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  <TrendingUp className="h-4.5 w-4.5 text-[#7A1E1E]" /> Measured
                  Impact Metrics
                </h3>
                <div className="max-w-full overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-[#7A1E1E] text-white">
                        <th className="px-6 py-3.5 text-xs font-semibold uppercase tracking-wider">
                          Impact Metric
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider">
                          Details / Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-zinc-150 divide-y">
                      {parseMetrics(program.project_metrics).map(
                        (metric, i) => (
                          <tr
                            key={i}
                            className="transition-colors hover:bg-zinc-50/50"
                          >
                            <td className="px-6 py-4 text-sm font-semibold text-zinc-700">
                              {metric.project_metric}
                            </td>
                            <td className="px-6 py-4 text-right text-sm font-black text-[#7A1E1E]">
                              {metric.details}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Before / After Transformation */}
            {(getBeforeUrls().length > 0 || getAfterUrls().length > 0) && (
              <div className="space-y-6 border-t border-zinc-100 pt-8">
                <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                  Transformation Media Gallery
                </h3>

                <div className="flex flex-col md:flex-row gap-0">
                  {/* Before State */}
                  {getBeforeUrls().length > 0 && (
                    <div className="flex-1 space-y-3 pr-0 md:pr-6">
                      <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider block">Initial State (Before)</span>
                      <div className="grid grid-cols-2 gap-3">
                        {getBeforeUrls().map((url, i) => (
                          <div
                            key={i}
                            className="group relative flex aspect-[4/3] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-zinc-200/60 bg-zinc-50 shadow-sm"
                            onClick={() => setLightboxUrl(url)}
                          >
                            {isVideoUrl(url) ? (
                              <div className="relative h-full w-full">
                                <video
                                  src={url}
                                  className="h-full w-full object-cover"
                                  muted
                                  playsInline
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/50">
                                  <Play className="h-10 w-10 fill-white/20 text-white" />
                                </div>
                              </div>
                            ) : (
                              <img
                                src={url}
                                alt={`Before transformation ${i + 1}`}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                              <div className="bg-white/20 text-white rounded-full p-3.5 backdrop-blur-md border border-white/30 transform scale-95 group-hover:scale-100 transition-transform duration-300 shadow-md">
                                <Eye className="h-6 w-6" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vertical Divider — only when both sections exist */}
                  {getBeforeUrls().length > 0 && getAfterUrls().length > 0 && (
                    <div className="hidden md:flex flex-col items-center justify-stretch mx-2">
                      <div className="w-px flex-1 bg-[#7A1E1E]/40 rounded-full" />
                    </div>
                  )}

                  {/* After State */}
                  {getAfterUrls().length > 0 && (
                    <div className="flex-1 space-y-3 pl-0 md:pl-6 mt-6 md:mt-0">
                      <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider block">Completed State (After)</span>
                      <div className="grid grid-cols-2 gap-3">
                        {getAfterUrls().map((url, i) => (
                          <div
                            key={i}
                            className="group relative flex aspect-[4/3] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-zinc-200/60 bg-zinc-50 shadow-sm"
                            onClick={() => setLightboxUrl(url)}
                          >
                            {isVideoUrl(url) ? (
                              <div className="relative h-full w-full">
                                <video
                                  src={url}
                                  className="h-full w-full object-cover"
                                  muted
                                  playsInline
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/50">
                                  <Play className="h-10 w-10 fill-white/20 text-white" />
                                </div>
                              </div>
                            ) : (
                              <img
                                src={url}
                                alt={`After transformation ${i + 1}`}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                              <div className="bg-white/20 text-white rounded-full p-3.5 backdrop-blur-md border border-white/30 transform scale-95 group-hover:scale-100 transition-transform duration-300 shadow-md">
                                <Eye className="h-6 w-6" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>

      {/* Lightbox Modal */}
      {lightboxUrl && (
        <div
          className="animate-in fade-in fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-black/90 backdrop-blur-md transition-all duration-300"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/10 p-2.5 text-white shadow-lg transition-colors hover:bg-white/20"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="h-6 w-6" />
          </button>

          <div
            className="relative max-h-[85vh] max-w-[90vw]"
            onClick={e => e.stopPropagation()}
          >
            {isVideoUrl(lightboxUrl) ? (
              <video
                src={lightboxUrl}
                className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
                controls
                autoPlay
              />
            ) : (
              <img
                src={lightboxUrl}
                alt="Enlarged view"
                className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
